// Billing screen for one operation (Work Orders: the op panel's Billing / "Bill now", and an
// op row's "Bill Operation" button): labor and parts lines with suggested amounts, billing
// history for the op code, DockMaster invoice reconciliation, Save draft / Record billing.
// Split out of app.js (2026-10-03). The app.js helpers it calls come in through
// setBillingDeps() (see estimates.js for why it doesn't import app.js back).
import { techLabel } from './techNames';
import { billedJobsByOpCode, billingStats } from './billing-history';
import { usesDockmaster } from './marinaConfig';
import { auth, database } from './initFirebase';
import { update } from './trackedDb';
import { toastMessage, woLabel } from './utilities';
import { ref, get } from 'firebase/database';

let shell = null;
export function setBillingDeps(deps) { shell = deps; }

// Same lazy-populated/fallback pattern as
// shrinkwrapLabor() — localStorage.userInfo is only set by checkAuthState(),
// a path separate from the normal login boot flow, so it isn't guaranteed to
// have run yet; fall back to the live Firebase auth user (same .displayName shape).
function getBillingUserDisplayName() {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null') || auth.currentUser;
  return userInfo?.displayName || '';
}

// Labor + parts $ suggestions (design_handoff_billing §4) — chip-only, never
// auto-filled into a saved field. Labor prefers a flat rate when the op code
// has one and the data it needs is available, else falls back to hours ×
// the plain hourly rate. Parts sums resolved inventory prices plus any
// dollar amount typed directly into an unresolved (Misc) part number.
//
// Note: the op-code catalog's Engine/Battery flat-rate types need a per-job
// engine/battery count, but that count is only ever entered once on the WO
// creation form and never persisted to the job record — so those two flat
// -rate types can't be reconstructed here and fall back to hours × rate too.
async function computeBillingSuggestions(jobData) {
  const rate = Number(document.getElementById('currentLaborRate')?.value) || 0;
  const laborEntries = Object.values(jobData.Labor || {});
  const hours = laborEntries.reduce((sum, l) => sum + (Number(l.Hours) || 0), 0);

  let flatAmount = 0;
  let flatLabel = '';
  try {
    const [opCodes, customerRecords] = await Promise.all([
      shell.fetchDataFromStore('opCodeData'),
      shell.fetchDataFromStore('customerData'),
    ]);
    const opCodeRecord = opCodes.find(o => o.OpCode === jobData.OpCode);
    const frLen = opCodeRecord?.Flat_Rate_Length;
    if (frLen && typeof frLen === 'string') {
      const customer = customerRecords.find(c => c.Customer_ID === jobData.Customer_ID);
      const boat = customer?.Boats ? customer.Boats[jobData.Boat_ID] : null;
      const boatLength = Number(boat?.Boat_Length) || 0;
      const perFoot = Number(opCodeRecord.Flat_Rate_Per_Foot) || 0;
      const laborFlat = Number(opCodeRecord.Flat_Rate_Labor) || 0;

      if (frLen.includes(',') && boatLength > 0 && perFoot > 0) {
        flatAmount = boatLength * perFoot;
        flatLabel = `Flat rate · ${boatLength}' × $${perFoot}/ft`;
      } else if (frLen.includes('Mast') && boatLength > 0 && perFoot > 0) {
        flatAmount = (boatLength * 1.3) * perFoot;
        flatLabel = `Flat rate · ${boatLength}' × 1.3 × $${perFoot}/ft`;
      } else if (frLen.includes('Generator') && laborFlat > 0) {
        flatAmount = laborFlat;
        flatLabel = 'Flat rate · generator';
      }
    }
  } catch (error) {
    console.error('Billing: flat-rate lookup failed', error);
  }

  const suggestedLabor = flatAmount > 0 ? flatAmount : (rate > 0 ? hours * rate : 0);
  const laborMathLabel = flatAmount > 0 ? flatLabel : (rate > 0 ? `${hours.toFixed(1)} hrs × $${rate}` : 'no rate on file');

  let inventory = [];
  try {
    inventory = await shell.fetchDataFromStore('inventoryData');
  } catch (error) {
    console.error('Billing: inventory lookup failed', error);
  }
  const priceByPartNumber = new Map();
  inventory.forEach(item => {
    const pn = String(item.Part_Number || '').trim();
    if (pn) priceByPartNumber.set(pn, Number(item.Sale_Price) || 0);
  });

  const partsEntries = Object.values(jobData.Parts || {});
  let resolvedTotal = 0;
  let handPricedCount = 0;
  partsEntries.forEach(p => {
    const pn = String(p.Part_Number || '').trim();
    const qty = Number(p.Quantity) || 0;
    if (priceByPartNumber.has(pn)) {
      resolvedTotal += qty * priceByPartNumber.get(pn);
    } else {
      const match = pn.match(/\$\s*([\d.]+)/);
      if (match) resolvedTotal += Number(match[1]) || 0;
      handPricedCount++;
    }
  });
  const suggestedParts = resolvedTotal;
  const partsMathLabel = handPricedCount > 0
    ? `${partsEntries.length} lines · ${handPricedCount} priced by hand`
    : (partsEntries.length > 0 ? `${partsEntries.length} lines · inventory price` : '');

  return { rate, hours, priceByPartNumber, suggestedLabor, laborMathLabel, suggestedParts, partsMathLabel };
}

// Read-only visual helper for the billing evidence cards — same avatar/date
// formatting as appendLaborDataToDiv() in the op-detail panel, but with no
// contentEditable/delete wiring since billing never edits Labor/Parts.
// `rate` is the plain hourly rate (#currentLaborRate) — the per-row $ is
// always hours × rate, independent of any flat-rate override the right
// rail's suggestion chip may use for the whole op.
function buildBillingLaborRow(entry, rate) {
  const row = document.createElement('div');
  row.className = 'billing-labor-row';

  const top = document.createElement('div');
  top.className = 'billing-labor-row-top';

  const avatar = document.createElement('div');
  avatar.className = 'billing-labor-avatar';
  avatar.textContent = (entry.Tech || '').trim().charAt(0).toUpperCase();

  const tech = document.createElement('span');
  tech.className = 'billing-labor-tech';
  tech.textContent = techLabel(entry.Tech);

  const date = document.createElement('span');
  date.className = 'billing-labor-date';
  const ts = entry.Time_Submitted || '';
  if (ts) {
    const parts = String(ts).split('-');
    date.textContent = (parts[1] && parts[2]) ? `${parts[1]}/${parts[2]}` : ts;
  }

  const hoursBlock = document.createElement('div');
  hoursBlock.className = 'billing-labor-hours-block';
  const hours = document.createElement('span');
  hours.className = 'billing-labor-hours';
  hours.textContent = entry.Hours ?? '';
  const hoursUnit = document.createElement('span');
  hoursUnit.className = 'billing-labor-hours-unit';
  hoursUnit.textContent = ' hrs';
  hoursBlock.append(hours, hoursUnit);

  top.append(avatar, tech, date, hoursBlock);

  const hoursNum = Number(entry.Hours) || 0;
  if (rate > 0 && hoursNum > 0) {
    const amountBlock = document.createElement('div');
    amountBlock.className = 'billing-labor-amount-block';
    const amount = document.createElement('span');
    amount.className = 'billing-labor-amount';
    amount.textContent = `$${(hoursNum * rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const rateSub = document.createElement('span');
    rateSub.className = 'billing-labor-rate-sub';
    rateSub.textContent = `@ $${rate}`;
    amountBlock.append(amount, rateSub);
    top.appendChild(amountBlock);
  }

  const desc = document.createElement('p');
  desc.className = 'billing-labor-desc';
  desc.textContent = entry.Description || '';

  row.append(top, desc);
  return row;
}

// `priceByPartNumber` is a Map of trimmed Part_Number → Sale_Price built once
// per modal open from the inventoryData IndexedDB store (§4 of the handoff).
function buildBillingPartRow(entry, priceByPartNumber) {
  const row = document.createElement('div');
  row.className = 'billing-part-row';

  const numberCol = document.createElement('div');
  numberCol.className = 'billing-part-number-col';

  const numberRow = document.createElement('div');
  numberRow.className = 'billing-part-number-row';

  const numberSpan = document.createElement('span');
  numberSpan.className = 'billing-part-number';
  numberSpan.textContent = entry.Part_Number || '';
  numberRow.appendChild(numberSpan);

  const isMisc = shell.isMiscPartNumber(entry.Part_Number);
  if (isMisc) {
    const badge = document.createElement('span');
    badge.className = 'billing-part-misc-badge';
    badge.textContent = 'Misc';
    numberRow.appendChild(badge);
  }
  numberCol.appendChild(numberRow);

  const desc = document.createElement('div');
  desc.className = 'billing-part-desc';
  desc.textContent = entry.Description || '';
  numberCol.appendChild(desc);

  const qty = document.createElement('span');
  qty.className = 'billing-part-qty';
  const qtyNum = Number(entry.Quantity) || 0;
  qty.textContent = entry.Quantity ?? '';

  const unitPrice = priceByPartNumber.get(String(entry.Part_Number || '').trim());

  const unit = document.createElement('span');
  unit.className = 'billing-part-unit';
  unit.textContent = unitPrice != null ? `$${Number(unitPrice).toFixed(2)}` : '—';

  const line = document.createElement('span');
  line.className = 'billing-part-line';
  if (unitPrice != null) {
    line.textContent = `$${(qtyNum * Number(unitPrice)).toFixed(2)}`;
  } else {
    // Unresolved (typically hand-typed, e.g. "electrical connectors $80") —
    // pull a dollar amount out of the string itself if there is one, rather
    // than guess a line total.
    const match = String(entry.Part_Number || '').match(/\$\s*([\d.]+)/);
    line.textContent = match ? `$${Number(match[1]).toFixed(2)}` : '—';
  }

  row.append(numberCol, qty, unit, line);
  return row;
}

// Collaboration: someone else may have recorded billing while this screen was open. Ask before
// replacing their figures with this user's.
async function confirmIfBilledMeanwhile(jobRef, jobData) {
  const live = (await get(jobRef)).val() || {};
  if (!live.Billing_Updated || live.Billing_Updated === jobData.Billing_Updated) return true;
  return confirm(`${live.Billed_By || 'Someone else'} just recorded billing on this operation (labor $${live.Labor_Billed || 0}, parts $${live.Parts_Billed || 0}).

Replace it with yours?`);
}

export async function billingModal(jobData) {

  // The modal's ids (#billing-labor, #billing-parts, ...) are looked up globally by its own
  // handlers, so a second copy — e.g. a double-click on a "Bill Operation" row button — would
  // have those lookups silently land on the first copy's inputs. This check runs before the
  // first await, and the modal is appended synchronously below, so it reliably catches the
  // second call.
  if (document.getElementById('billing-modal')) return;

  // Every write below targets this job. It used to be read back from the op-detail panel's
  // hidden #jobId field, which only points at this job if that panel happens to be open on it —
  // opening billing from anywhere else would have saved to whatever op was last viewed instead.
  const jobId = jobData.Job_ID;
  if (!jobId) {
    console.error('billingModal: job data has no Job_ID', jobData);
    toastMessage('Could not open billing for this operation.', 'red', 2500);
    return;
  }

  const modal = document.createElement("div");
  modal.className = "billing-surface";
  modal.id = "billing-modal";

  modal.innerHTML = `
      <div class="billing-topbar">
        <button class="exitButton billing-back" data-close="billing-modal"><img src="/assets/close_icon.png" alt="Close Icon" width="24" height="24"></button>
        <div class="billing-breadcrumb">
          <span class="billing-crumb-dim">Work Orders</span>
          <span class="billing-crumb-sep">/</span>
          <span class="billing-crumb-dim" id="billingCustomerCrumb"></span>
          <span class="billing-crumb-sep">/</span>
          <span class="billing-crumb-title" id="billingTitleCrumb"></span>
        </div>
        <div class="billing-topbar-actions">
          <span class="billing-status-pill" id="billingStatusPill">
            <span class="billing-status-dot"></span>
            <span id="billingStatusText"></span>
          </span>
          <button type="button" class="billing-ghost-btn" id="saveBilling">Save draft</button>
          <button type="button" class="billing-primary-btn" id="completeBilling">Record billing</button>
        </div>
      </div>

      <div id="billing-body">

        <div class="billing-context-strip">
          <div class="billing-context-boat-icon"><img src="/assets/boat.png" width="24" height="24" alt=""></div>
          <div class="billing-context-boat-block">
            <div class="billing-context-boat-row">
              <span class="billing-context-boat-name" id="billingBoatName"></span>
              <span class="billing-context-wo" id="billingWorkOrder"></span>
            </div>
            <div class="billing-context-customer" id="billingCustomer"></div>
          </div>
          <div class="billing-context-divider"></div>
          <div class="billing-context-op-block">
            <div class="billing-context-op-row">
              <span class="billing-context-op-chip" id="billingOpChip"></span>
              <span class="billing-context-op-desc" id="billingOpDesc"></span>
            </div>
            <div class="billing-context-meta" id="billingMeta"></div>
          </div>
          <div class="billing-context-totals">
            <div class="billing-context-total"><div class="billing-context-total-num" id="billingHoursTotal">0.0</div><div class="billing-context-total-label">Hours</div></div>
            <div class="billing-context-total-divider"></div>
            <div class="billing-context-total"><div class="billing-context-total-num" id="billingPartsTotal">0</div><div class="billing-context-total-label">Part lines</div></div>
          </div>
        </div>

        <div class="billing-columns">

          <div class="billing-evidence-col">
            <div id="billingVarianceBanners"></div>
            <div class="billing-card">
              <div class="billing-card-header">
                <span class="billing-card-title">Labor</span>
                <span class="billing-card-summary" id="billingLaborSummary"></span>
                <span class="billing-card-subtotal" id="billingLaborSubtotal"></span>
              </div>
              <div class="billing-card-body" id="billingLaborBody"></div>
            </div>

            <div class="billing-card">
              <div class="billing-card-header">
                <span class="billing-card-title">Parts</span>
                <span class="billing-card-summary" id="billingPartsSummary"></span>
                <span class="billing-card-subtotal" id="billingPartsSubtotal"></span>
              </div>
              <div class="billing-card-body" id="billingPartsBody">
                <div class="billing-part-columns-header">
                  <span>Part</span>
                  <span>Qty</span>
                  <span>Unit</span>
                  <span>Line</span>
                </div>
                <div id="billingPartsRows"></div>
              </div>
            </div>

            <div class="billing-card" id="billingReconciliationCard" style="display:none;">
              <div class="billing-card-header">
                <span class="billing-card-title">Recorded vs invoiced</span>
                <span class="billing-card-summary" id="billingReconSummary"></span>
              </div>
              <div class="billing-card-body" id="billingReconBody"></div>
            </div>
          </div>

          <div class="billing-rail">
            <div class="billing-card billing-rail-card">
              <div class="billing-rail-card-header">
                <div class="billing-rail-card-title">What gets billed</div>
                <div class="billing-rail-card-sub">Saved to the job record — nothing leaves for DockMaster.</div>
              </div>
              <div class="billing-rail-card-body">
                <div class="billing-field">
                  <div class="billing-field-label-row">
                    <label for="billing-labor">Labor billed</label>
                    <span class="billing-field-math" id="billingLaborMath"></span>
                  </div>
                  <div class="billing-field-input-row">
                    <div class="billing-dollar-input"><span>$</span><input type="number" id="billing-labor" placeholder="0.00" value="${jobData.Labor_Billed ?? ''}"></div>
                  </div>
                </div>
                <div class="billing-field">
                  <div class="billing-field-label-row">
                    <label for="billing-parts">Parts billed</label>
                    <span class="billing-field-math" id="billingPartsMath"></span>
                  </div>
                  <div class="billing-field-input-row">
                    <div class="billing-dollar-input"><span>$</span><input type="number" id="billing-parts" placeholder="0.00" value="${jobData.Parts_Billed ?? ''}"></div>
                  </div>
                </div>
                <div class="billing-job-total-row">
                  <span>Job total</span>
                  <span id="billingJobTotal">$0.00</span>
                </div>
              </div>
            </div>

            <div class="billing-card billing-rail-card">
              <div class="billing-rail-card-header">
                <div class="billing-rail-card-title">Description <span class="billing-chip-blue">Customer sees this</span></div>
                <div class="billing-rail-card-sub">This is what shows in the customer's service history.</div>
              </div>
              <div class="billing-rail-card-body">
                <textarea id="billing-description" class="billing-textarea"></textarea>
                <div class="billing-ghost-actions" id="billingDescriptionActions"></div>
              </div>
            </div>

            <div class="billing-card billing-rail-card">
              <div class="billing-rail-card-header">
                <div class="billing-rail-card-title">Internal notes</div>
                <div class="billing-rail-card-sub">Stays in the app.</div>
              </div>
              <div class="billing-rail-card-body">
                <textarea id="billing-notes" class="billing-textarea"></textarea>
              </div>
            </div>

            <div class="billing-card billing-rail-card billing-record-card">
              <div class="billing-rail-card-header">
                <div class="billing-rail-card-title">Record</div>
              </div>
              <div class="billing-rail-card-body billing-record-rows">
                <div class="billing-record-row"><span>Billed by</span><span id="billingRecordBilledBy">—</span></div>
                <div class="billing-record-row"><span>Billed on</span><span id="billingRecordBilledOn">—</span></div>
                <div class="billing-record-row"><span>Rate used</span><span id="billingRecordRateUsed">—</span></div>
              </div>
            </div>
          </div>

        </div>
      </div>
  `;

  document.body.appendChild(modal);

  modal.querySelector('#billing-description').value = jobData.Billing_Description || '';
  modal.querySelector('#billing-notes').value = jobData.Billing_Notes || '';

  // This modal is built and appended long after the app's one-time
  // DOMContentLoaded '.exitButton' wiring ran, so its close (x) button
  // needs its own direct listener or clicking it silently does nothing
  // (same fix as openWoInvoicePreview()'s close button).
  modal.querySelector('[data-close="billing-modal"]')?.addEventListener('click', () => modal.remove());

  // Breadcrumb + status pill
  modal.querySelector('#billingCustomerCrumb').textContent = jobData.Customer_Name || '';
  modal.querySelector('#billingTitleCrumb').textContent = `Billing · Op ${jobData.OpCode || ''}`;

  const isBilled = !!jobData.Billing_Completed;
  const billedDate = isBilled ? new Date(jobData.Billing_Completed) : null;
  const billedText = billedDate && !isNaN(billedDate) ? billedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const statusPill = modal.querySelector('#billingStatusPill');
  statusPill.classList.toggle('billing-tone-navy', isBilled);
  modal.querySelector('#billingStatusText').textContent = isBilled ? `Billed${billedText ? ' ' + billedText : ''}` : 'Ready to bill';

  // Record card (§3) — additive fields, all optional on read; jobs billed
  // before this shipped simply show "—" rather than blocking on anything.
  modal.querySelector('#billingRecordBilledBy').textContent = jobData.Billed_By || '—';
  modal.querySelector('#billingRecordBilledOn').textContent = billedText || '—';
  modal.querySelector('#billingRecordRateUsed').textContent = jobData.Billed_Rate != null ? `$${Number(jobData.Billed_Rate).toFixed(2)}/hr` : '—';

  // Context strip — same text the op-detail panel shows (shell.buildOpContextText() is shared), built
  // from this job's own data rather than read back off that panel, so it's right no matter where
  // billing was opened from. Customer lookup only adds the phone number; a miss just omits it.
  let customerData = null;
  try {
    customerData = (await shell.getCustomerDataById(jobData.Customer_ID, jobData.Boat_ID))?.customerData || null;
  } catch (error) {
    console.warn('Billing: customer lookup for context strip failed', error);
  }
  const ctx = shell.buildOpContextText(jobData, customerData);
  modal.querySelector('#billingBoatName').textContent = ctx.boatName;
  modal.querySelector('#billingWorkOrder').textContent = ctx.workOrder;
  modal.querySelector('#billingCustomer').textContent = ctx.customer;
  modal.querySelector('#billingOpChip').textContent = ctx.opChip;
  modal.querySelector('#billingOpDesc').textContent = ctx.opDesc;
  modal.querySelector('#billingMeta').textContent = ctx.meta;

  // Labor: sort newest-first, aggregate hours + per-tech split from the
  // same jobData.Labor the old grid read from — no new data source.
  const laborEntries = Object.values(jobData.Labor || {})
    .sort((a, b) => String(b.Time_Submitted || '').localeCompare(String(a.Time_Submitted || '')));
  const totalHours = laborEntries.reduce((sum, l) => sum + (Number(l.Hours) || 0), 0);
  const perTech = {};
  laborEntries.forEach(l => {
    const t = techLabel(l.Tech) || '—';
    perTech[t] = (perTech[t] || 0) + (Number(l.Hours) || 0);
  });
  const perTechText = Object.entries(perTech).map(([t, h]) => `${t} ${h.toFixed(1)}`).join(' · ');

  modal.querySelector('#billingHoursTotal').textContent = totalHours.toFixed(1);
  modal.querySelector('#billingLaborSummary').textContent =
    `${laborEntries.length} ${laborEntries.length === 1 ? 'entry' : 'entries'} · ${totalHours.toFixed(1)} hrs${perTechText ? ' · ' + perTechText : ''}`;

  // Suggestions (§4) — computed once, up front, so the evidence cards' own
  // per-row/subtotal $ figures and the right rail's chip + math label all
  // agree with each other.
  const suggestions = await computeBillingSuggestions(jobData);

  const laborBody = modal.querySelector('#billingLaborBody');
  if (laborEntries.length === 0) {
    laborBody.innerHTML = '<p class="billing-empty-state">No labor logged yet.</p>';
  } else {
    laborEntries.forEach(entry => laborBody.appendChild(buildBillingLaborRow(entry, suggestions.rate)));
  }
  const laborSubtotal = suggestions.rate > 0 ? totalHours * suggestions.rate : 0;
  modal.querySelector('#billingLaborSubtotal').textContent = laborSubtotal > 0
    ? `$${laborSubtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  // Parts: same source data as the old grid, plus the existing
  // shell.isMiscPartNumber() classification already used in the op-detail panel.
  const partsEntries = Object.values(jobData.Parts || {});
  const totalUnits = partsEntries.reduce((sum, p) => sum + (Number(p.Quantity) || 0), 0);

  modal.querySelector('#billingPartsTotal').textContent = String(partsEntries.length);
  modal.querySelector('#billingPartsSummary').textContent =
    `${partsEntries.length} ${partsEntries.length === 1 ? 'line' : 'lines'} · ${totalUnits} units`;

  const partsRows = modal.querySelector('#billingPartsRows');
  if (partsEntries.length === 0) {
    modal.querySelector('#billingPartsBody').innerHTML = '<p class="billing-empty-state">No parts logged yet.</p>';
  } else {
    partsEntries.forEach(entry => partsRows.appendChild(buildBillingPartRow(entry, suggestions.priceByPartNumber)));
  }
  modal.querySelector('#billingPartsSubtotal').textContent = suggestions.suggestedParts > 0
    ? `$${suggestions.suggestedParts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  // Right rail — math label + "Use $X" chip. Chip-only, never auto-fills:
  // an empty field stays empty until a person clicks it.
  modal.querySelector('#billingLaborMath').textContent = suggestions.suggestedLabor > 0 ? suggestions.laborMathLabel : (suggestions.rate > 0 ? '' : 'no rate on file');
  modal.querySelector('#billingPartsMath').textContent = suggestions.partsMathLabel;

  const addUseChip = (inputEl, amount) => {
    if (!(amount > 0)) return;
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'billing-use-chip';
    chip.textContent = `Use $${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    chip.addEventListener('click', () => {
      inputEl.value = amount.toFixed(2);
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    });
    inputEl.closest('.billing-field-input-row').appendChild(chip);
  };

  // Job total — display only, purely derived from the two saved fields
  // live as they're typed; never written anywhere itself.
  const laborInput = modal.querySelector('#billing-labor');
  const partsInput = modal.querySelector('#billing-parts');
  const jobTotalEl = modal.querySelector('#billingJobTotal');
  const updateJobTotal = () => {
    const total = (Number(laborInput.value) || 0) + (Number(partsInput.value) || 0);
    jobTotalEl.textContent = `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  laborInput.addEventListener('input', updateJobTotal);
  partsInput.addEventListener('input', updateJobTotal);
  updateJobTotal();

  addUseChip(laborInput, suggestions.suggestedLabor);
  addUseChip(partsInput, suggestions.suggestedParts);

  // Description ghost actions (§4) — both purely additive to the textarea,
  // editable after, never sent anywhere on their own.
  const descriptionActions = modal.querySelector('#billingDescriptionActions');
  const descriptionEl = modal.querySelector('#billing-description');

  if (laborEntries.length > 0) {
    const draftBtn = document.createElement('button');
    draftBtn.type = 'button';
    draftBtn.className = 'billing-ghost-action';
    draftBtn.textContent = `Draft from the ${laborEntries.length} labor ${laborEntries.length === 1 ? 'note' : 'notes'}`;
    draftBtn.addEventListener('click', () => {
      descriptionEl.value = laborEntries
        .slice()
        .reverse()
        .map(l => (l.Description || '').trim())
        .filter(Boolean)
        .join(' ');
    });
    descriptionActions.appendChild(draftBtn);
  }

  // History lookup (§4) — same billedJobsByOpCode() helper the variance
  // check uses, shared with the (later) estimate-builder "start from a past
  // job" path. Fewer than 3 comparable jobs → no variance banner at all.
  let history = [];
  try {
    history = jobData.OpCode ? await billedJobsByOpCode(jobData.OpCode) : [];
  } catch (error) {
    console.error('Billing: history lookup failed', error);
  }

  const lastWithWording = history.find(h => h.Billing_Description);
  if (lastWithWording) {
    const reuseBtn = document.createElement('button');
    reuseBtn.type = 'button';
    reuseBtn.className = 'billing-ghost-action';
    reuseBtn.textContent = `Reuse last ${jobData.OpCode} wording`;
    reuseBtn.addEventListener('click', () => {
      descriptionEl.value = lastWithWording.Billing_Description;
    });
    descriptionActions.appendChild(reuseBtn);
  }

  // Variance banner(s) (§4) — advisory only, never blocks Record billing.
  // Say nothing rather than something wrong: fewer than 3 comparable jobs
  // skips the hours-variance check entirely.
  const varianceBanners = [];
  if (history.length >= 3) {
    const stats = billingStats(history);
    if (stats.medianHours > 0 && (totalHours > 1.4 * stats.medianHours || totalHours < 0.6 * stats.medianHours)) {
      const multiple = totalHours / stats.medianHours;
      varianceBanners.push({
        headline: `${totalHours.toFixed(1)} hrs is ${multiple.toFixed(1)}× the usual for op ${jobData.OpCode}`,
        detail: `Last ${history.length} billed ${jobData.OpCode}s ran ${stats.minHours.toFixed(1)}–${stats.maxHours.toFixed(1)} hrs (median ${stats.medianHours.toFixed(1)}).`
      });
    }
  }
  const undescribedLabor = laborEntries.find(l => (Number(l.Hours) || 0) > 0 && !(l.Description || '').trim());
  if (undescribedLabor) {
    varianceBanners.push({
      headline: `${techLabel(undescribedLabor.Tech) || 'A labor entry'} logged ${undescribedLabor.Hours} hrs with no description`,
      detail: 'Worth a line before this goes out — it\'s what shows on the customer\'s invoice history.'
    });
  }
  const unpricedPart = partsEntries.find(p => {
    const pn = String(p.Part_Number || '').trim();
    return !suggestions.priceByPartNumber.has(pn) && !/\$\s*[\d.]+/.test(pn);
  });
  if (unpricedPart) {
    varianceBanners.push({
      headline: `${unpricedPart.Part_Number || 'A part line'} has no resolvable price`,
      detail: 'Not in inventory and no dollar amount typed in — it\'ll show as "—" until priced by hand.'
    });
  }

  const bannerContainer = modal.querySelector('#billingVarianceBanners');
  varianceBanners.forEach(b => {
    const banner = document.createElement('div');
    banner.className = 'billing-variance-banner';
    banner.innerHTML = `
      <div class="billing-variance-icon">!</div>
      <div class="billing-variance-text">
        <div class="billing-variance-headline"></div>
        <div class="billing-variance-detail"></div>
      </div>
    `;
    banner.querySelector('.billing-variance-headline').textContent = b.headline;
    banner.querySelector('.billing-variance-detail').textContent = b.detail;
    bannerContainer.appendChild(banner);
  });

  // Invoice reconciliation (§5) — read-only, reuses
  // shell.lookupDockmasterInvoiceForWorkOrder() (the same lookup
  // openWoInvoicePreview() uses) rather than a second copy. Lazy: only
  // fires when the job already has a recorded amount; a lookup failure
  // never blocks Save/Record and never touches Labor_Billed/Parts_Billed.
  const reconCard = modal.querySelector('#billingReconciliationCard');
  const reconBody = modal.querySelector('#billingReconBody');
  const reconSummary = modal.querySelector('#billingReconSummary');

  async function runBillingReconciliation() {
    reconCard.style.display = '';
    reconBody.innerHTML = '<p class="billing-empty-state">Checking DockMaster…</p>';
    reconSummary.textContent = '';

    const recordedTotal = (Number(laborInput.value) || 0) + (Number(partsInput.value) || 0);

    try {
      const { match, lineItems } = await shell.lookupDockmasterInvoiceForWorkOrder(jobData.Work_Order, jobData.Customer_ID);

      if (!match) {
        reconBody.innerHTML = `
          <p class="billing-empty-state">Not invoiced in DockMaster yet.</p>
          <div class="billing-recon-actions">
            <button type="button" class="billing-recon-mark-btn" disabled>Mark reconciled</button>
          </div>
        `;
        return;
      }

      // Per-operation match, parsed off the leading "OpCode: ..." token
      // DockMaster's LongDesc carries — fall back to the WO total rather
      // than guess when nothing parses to this op's code.
      const opLine = lineItems.find(li => (li.Description || '').split(':')[0].trim() === String(jobData.OpCode || '').trim());
      const usedFallback = !opLine;
      const invoicedAmount = opLine
        ? (parseFloat(String(opLine.Charge).replace(/[^0-9.-]/g, '')) || 0)
        : lineItems.reduce((sum, li) => sum + (parseFloat(String(li.Charge).replace(/[^0-9.-]/g, '')) || 0), 0);
      const diff = invoicedAmount - recordedTotal;
      const isMatch = Math.abs(diff) < 0.01;

      reconSummary.textContent = `Invoice ${match.Id}`;

      if (isMatch) {
        reconBody.innerHTML = `<p class="billing-recon-match">Invoice ${match.Id} · $${invoicedAmount.toFixed(2)} · matches the record.</p>`;
      } else {
        reconBody.innerHTML = `
          <div class="billing-recon-row billing-recon-header">
            <span>${usedFallback ? 'Work order' : 'Operation'}</span><span>Recorded</span><span>Invoiced</span><span>Difference</span>
          </div>
          <div class="billing-recon-row">
            <span>${usedFallback ? woLabel(jobData.Work_Order) : `Op ${jobData.OpCode}`}</span>
            <span>$${recordedTotal.toFixed(2)}</span>
            <span>$${invoicedAmount.toFixed(2)}</span>
            <span class="billing-recon-diff">${diff >= 0 ? '+' : '−'}$${Math.abs(diff).toFixed(2)}</span>
          </div>
        `;
      }

      const actions = document.createElement('div');
      actions.className = 'billing-recon-actions';
      const markBtn = document.createElement('button');
      markBtn.type = 'button';
      markBtn.className = 'billing-recon-mark-btn';
      markBtn.textContent = 'Mark reconciled';
      markBtn.addEventListener('click', async () => {
        try {
          await update(ref(database, `/data/${jobId}`), {
            Invoice_Number: match.Id,
            Invoiced_Amount: invoicedAmount,
            Invoice_Checked_On: await shell.getTodaysDate(),
            Reconciled_By: getBillingUserDisplayName()
          });
          toastMessage('Marked reconciled', 'green', 2000);
          markBtn.disabled = true;
        } catch (error) {
          console.error('Billing: mark reconciled failed', error);
          toastMessage('Could not save reconciliation. Please try again.', 'red', 2000);
        }
      });
      actions.appendChild(markBtn);
      reconBody.appendChild(actions);

      if (jobData.Invoice_Number) {
        const note = document.createElement('div');
        note.className = 'billing-recon-reconciled-note';
        note.textContent = `Already reconciled by ${jobData.Reconciled_By || 'someone'}${jobData.Invoice_Checked_On ? ' on ' + jobData.Invoice_Checked_On : ''}.`;
        reconBody.appendChild(note);
        markBtn.disabled = true;
      }
    } catch (error) {
      // Never blocks saving — log only, no toast storm.
      console.error('Billing: DockMaster reconciliation lookup failed', error);
      reconBody.innerHTML = `<p class="billing-empty-state">Recorded $${recordedTotal.toFixed(2)}. Couldn't reach DockMaster — <button type="button" class="billing-ghost-action" id="billingReconRetry">retry</button></p>`;
      reconBody.querySelector('#billingReconRetry')?.addEventListener('click', runBillingReconciliation);
    }
  }

  // "Job not yet billed" → panel hidden entirely, per §5's state table. Marinas that don't use
  // DockMaster (Settings › Features › Billing system) never see it.
  if (isBilled && usesDockmaster()) {
    runBillingReconciliation();
  } else {
    reconCard.style.display = 'none';
  }

  // Save draft — today's #saveBilling handler, same four fields, no status
  // change, plus the additive Billed_By / Billing_Updated fields (§3).
  modal.querySelector("#saveBilling").addEventListener("click", async () => {
      const laborBilled = document.getElementById("billing-labor").value;
      const partsBilled = document.getElementById("billing-parts").value;
      const billingNotes = document.getElementById("billing-notes").value;
      const billingDescription = document.getElementById("billing-description").value;

      const billingData = {
          Labor_Billed: laborBilled,
          Parts_Billed: partsBilled,
          Billing_Notes: billingNotes,
          Billing_Description: billingDescription,
          Billed_By: getBillingUserDisplayName(),
          Billing_Updated: new Date().toISOString()
      }

      try {
        const jobRef = ref(database, `/data/${jobId}`);
        if (!(await confirmIfBilledMeanwhile(jobRef, jobData))) return;
        await update(jobRef, billingData);
        jobData.Billing_Updated = billingData.Billing_Updated; // this screen's own save isn't "someone else's"
        toastMessage("Billing information saved successfully", 'green', 2000);
      }catch(error){
        console.error("Error saving billing information", error);
        toastMessage("Failed to save billing information. Please try again.", 'red', 2000);
      }

      modal.remove();
  });

  // Record billing — today's #completeBilling handler, same additional
  // Billing_Completed + Status:"Closed" write, plus the additive record
  // fields (§3): who billed it, the rate/hours the amount was based on, and
  // what the app suggested next to what was actually entered.
  modal.querySelector("#completeBilling").addEventListener("click", async () => {
      const laborBilled = document.getElementById("billing-labor").value;
      const partsBilled = document.getElementById("billing-parts").value;
      const billingNotes = document.getElementById("billing-notes").value;
      const billingDescription = document.getElementById("billing-description").value;

      const nowIso = new Date().toISOString();

      const billingData = {
          Labor_Billed: laborBilled,
          Parts_Billed: partsBilled,
          Billing_Notes: billingNotes,
          Billing_Completed: await shell.getTodaysDate(),
          Billing_Description: billingDescription,
          Status: "Closed",
          Billed_By: getBillingUserDisplayName(),
          Billing_Updated: nowIso,
          Billed_Rate: suggestions.rate,
          Hours_Billed: totalHours,
          Suggested_Labor: suggestions.suggestedLabor,
          Suggested_Parts: suggestions.suggestedParts
      };

      try {
        const jobRef = ref(database, `/data/${jobId}`);
        if (!(await confirmIfBilledMeanwhile(jobRef, jobData))) return;
        await update(jobRef, billingData);
        jobData.Billing_Updated = billingData.Billing_Updated; // this screen's own save isn't "someone else's"
        shell.removeOperationDetails();
        modal.remove();
        toastMessage("Billing information saved successfully", 'green', 2000);
      }catch(error){
        console.error("Error saving billing information", error);
        toastMessage("Failed to save billing information. Please try again.", 'red', 2000);
      }

    });

}
